import {useQuery} from "@khan/data-gql";
import {Errors, KAError} from "@khan/logging";
import {useLocation} from "@khan/urls";

import {QA_EXPAND_KEY_INFO_QUERY} from "../queries.ts";
import type {QAExpandKeyInfo, Result} from "../types/types.ts";

type QAExpandKeyInfoReturn = [
    Result<QAExpandKeyInfo>,
    string | null | undefined,
];

/**
 * This hook queries for qaExpandKeyInfo for the qa_expand_key search parameter
 * in the URL. We use this data to link to individual comments within a
 * discussion thread.
 */
const useQAExpandKeyInfo = (): QAExpandKeyInfoReturn => {
    const {queryParams} = useLocation();
    const {qa_expand_key: key} = queryParams;
    const skipQuery = !key;
    const [result, refetch] = useQuery(QA_EXPAND_KEY_INFO_QUERY, {
        skip: skipQuery,
        variables: {encryptedKey: key},
    });

    if (skipQuery || result.status === "no-data") {
        return [{status: "skipped"}, key];
    }

    if (result.status !== "success") {
        return [result, key];
    }

    try {
        const {qaExpandKeyInfo} = result.data;
        if (!qaExpandKeyInfo) {
            throw new KAError(`No expand key info`, Errors.Internal, {
                sentryData: {
                    contexts: {
                        Data: {
                            qaExpandKey: key,
                        },
                    },
                },
            });
        }
        return [
            {
                status: "success",
                data: {
                    feedbackType: qaExpandKeyInfo.feedbackType,
                    unencryptedKey: qaExpandKeyInfo.unencryptedKey,
                },
                refetch,
            },
            key,
        ];
    } catch (error: any) {
        return [{status: "error", error}, key];
    }
};

export default useQAExpandKeyInfo;
